/**
 * Pure builder for the CAM (Credit Assessment Memo) view-model. Mirrors the
 * grouping + scoring logic of templates/case-details-tabs/cam-view-new.js, but
 * read-only and shaped for the Credit Checker section.
 */
import {
    CAM_CATEGORY_TITLE_MAP,
    CAM_SECTION_ICON_MAP,
    CAM_CATEGORY_ORDER
} from "./constants";

// camEngineData is a plain object in redux; guard for an Immutable shape too.
function toPlainObject(data) {
    if (!data) {
        return {};
    }
    if (typeof data.toJS === "function") {
        return data.toJS();
    }
    return data;
}

// RAG status for a category from its pass/fail counts (same rules as CAM View).
// `total` here is the count of APPLICABLE parameters only — "Not Applicable"
// rows are excluded upstream so they never drag a category into amber. A
// category with every applicable check passed (or with no applicable checks at
// all) is green.
function statusKeyFor(passed, failed, total) {
    if (passed === total) {
        return "GREEN";
    }
    if (failed > 0 && failed >= passed) {
        return "RED";
    }
    return "AMBER";
}

// A parameter counts toward the RAG scoring only when it produced a real
// verdict — "Not Applicable" parameters are ignored.
function isApplicable(row) {
    return row.output !== "Not Applicable";
}

function normaliseValue(factValue) {
    if (factValue === true) {
        return "True";
    }
    if (
        factValue === false ||
        factValue === "" ||
        factValue === null ||
        factValue === undefined
    ) {
        return "False";
    }
    return String(factValue);
}

function normalisePolicy(policy) {
    if (typeof policy === "boolean") {
        return String(policy);
    }
    return policy === null || policy === undefined || policy === ""
        ? "NA"
        : String(policy);
}

function normaliseOutput(output) {
    if (output === true) {
        return "Pass";
    }
    if (output === false) {
        return "Fail";
    }
    return "Not Applicable";
}

/**
 * True when the credit team has written a remark against this parameter. A
 * remarked failure no longer prompts "Requires Action" — the remark IS the
 * action. Whitespace-only text does not count.
 */
export function hasCreditRemark(row) {
    return Boolean(row && String(row.creditRemarks || "").trim());
}

// Failed parameters still awaiting a credit remark.
export function pendingActionRows(rows) {
    return (rows || []).filter(
        row => row.output === "Fail" && !hasCreditRemark(row)
    );
}

/**
 * @param {Object} camEngineData  raw cam-engine map keyed by rule id
 * @returns {Object} { hasData, summary, categories }
 */
export function buildCamAssessment(camEngineData) {
    const data = toPlainObject(camEngineData);
    const rows = [];
    Object.keys(data).forEach(key => {
        const el = data[key];
        if (!el || !el.category) {
            return;
        }
        rows.push({
            // Rule id — the key the CAM update payload is keyed on (key2), so
            // an edited credit remark maps back to the right parameter.
            key2: key,
            category: el.category,
            title: CAM_CATEGORY_TITLE_MAP[el.category] || "Others",
            factName: el.fact_name || "—",
            value: normaliseValue(el.fact_value),
            policy: normalisePolicy(el.policy),
            output: normaliseOutput(el.output),
            creditRemarks:
                el.credit_remarks || el.remarks || el.remark || ""
        });
    });

    const groups = {};
    rows.forEach(row => {
        if (!groups[row.title]) {
            groups[row.title] = [];
        }
        groups[row.title].push(row);
    });

    const orderIndex = title => {
        const i = CAM_CATEGORY_ORDER.indexOf(title);
        return i === -1 ? CAM_CATEGORY_ORDER.length : i;
    };
    const categories = Object.keys(groups)
        .map(title => {
            const groupRows = groups[title];
            // Score against applicable rows only; NA rows still render (as
            // disabled) but don't affect passed / total / status.
            const applicable = groupRows.filter(isApplicable);
            const passed = applicable.filter(r => r.output === "Pass").length;
            const failed = applicable.filter(r => r.output === "Fail").length;
            return {
                title,
                icon: CAM_SECTION_ICON_MAP[title] || "appstore",
                passed,
                failed,
                total: applicable.length,
                statusKey: statusKeyFor(passed, failed, applicable.length),
                rows: groupRows
            };
        })
        // Preferred section order (Applicant → GSTIN → Banking → Bureau →
        // Financials → Anchor), unlisted categories last.
        .sort((a, b) => orderIndex(a.title) - orderIndex(b.title));

    // Overall risk is scored on applicable parameters only — NA parameters are
    // not counted, so they neither help nor hurt the pass percentage.
    const applicableRows = rows.filter(isApplicable);
    const total = applicableRows.length;
    const pass = applicableRows.filter(r => r.output === "Pass").length;
    const fail = applicableRows.filter(r => r.output === "Fail").length;
    const passPct = total ? Math.round((pass / total) * 100) : 0;
    let riskLabel = "Low Risk";
    let riskTone = "good";
    if (passPct < 75 && passPct >= 50) {
        riskLabel = "Medium Risk";
        riskTone = "warn";
    }
    if (passPct < 50) {
        riskLabel = "High Risk";
        riskTone = "bad";
    }

    return {
        // Section presence is based on ANY rows (even all-NA); scoring above
        // uses applicable rows only.
        hasData: rows.length > 0,
        summary: { total, pass, fail, passPct, riskLabel, riskTone },
        categories
    };
}

export default buildCamAssessment;
